import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';
import { BiTrash, BiPlus, BiCheck, BiX } from 'react-icons/bi';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalAction } from '@components/ModalComponents';
import type { OneOffDaysManagerProps, OneOffEntry, ProcessedOneOffDay } from './OneOffDaysManager.types';

/** How many days in the past to keep showing entries */
const PAST_DISPLAY_DAYS = 7;

const OneOffDaysManager: React.FC<OneOffDaysManagerProps> = ({
  config,
  oneOffDays,
  onCreate,
  onDelete,
  loading,
  userName,
  open,
  onClose,
}) => {
  const { entityId, timezone, labels } = config;

  // Process existing one-off days for display
  const processedOneOffDays = React.useMemo((): ProcessedOneOffDay[] => {
    const cutoff = dayjs().subtract(PAST_DISPLAY_DAYS, 'day').startOf('day');
    return oneOffDays
      .filter((entry) => entry.type === 'one-off' && entry.day && entry.month && entry.year)
      .map((entry) => {
        const dateObj = new Date(entry.year!, entry.month! - 1, entry.day!);
        return {
          id: entry.id.toString(),
          name: entry.name,
          date: dayjs(dateObj).format('MMMM D, YYYY'),
          dateObj: dateObj,
          allDay: !entry.is_open && !entry.open_time,
          isWorking: !!entry.is_open,
          openTime: entry.open_time,
          closedTime: entry.closed_time,
        };
      })
      .filter((entry) => dayjs(entry.dateObj).isAfter(cutoff))
      .sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime());
  }, [oneOffDays]);

  const today = dayjs().startOf('day');
  const upcomingEntries = processedOneOffDays.filter((e) => !dayjs(e.dateObj).isBefore(today));
  const pastEntries = processedOneOffDays.filter((e) => dayjs(e.dateObj).isBefore(dayjs(), 'day'));

  // Form state for adding new entries
  const createBlankEntry = (): OneOffEntry => ({
    id: Date.now().toString(),
    name: '',
    date: null,
    allDay: true,
    isWorking: false,
    openTime: dayjs().hour(9).minute(0),
    closedTime: dayjs().hour(17).minute(0),
  });

  const [newEntries, setNewEntries] = React.useState<OneOffEntry[]>([createBlankEntry()]);

  // Hold a reload while the open dialog has a draft entry, or a save or delete runs.
  // `loading` is the parent's create/delete in flight.
  const hasDraftEntry = newEntries.some((e) => !!e.name.trim() || !!e.date);
  useReloadGuard(open && (hasDraftEntry || loading), 'Days off');

  const resetForm = () => {
    setNewEntries([createBlankEntry()]);
  };

  const handleAddEntry = () => {
    setNewEntries([...newEntries, createBlankEntry()]);
  };

  const handleDeleteEntry = (id: string) => {
    if (newEntries.length === 1) {
      toast.warning('You must have at least one entry');
      return;
    }
    setNewEntries(newEntries.filter((e) => e.id !== id));
  };

  const handleEntryChange = (
    id: string,
    field: keyof OneOffEntry,
    value: string | Dayjs | boolean | null
  ) => {
    setNewEntries(newEntries.map((e) => (e.id === id ? { ...e, [field]: value } : e)));
  };

  const handleToggleWorking = (id: string, isWorking: boolean) => {
    setNewEntries(newEntries.map((e) => {
      if (e.id !== id) return e;
      return {
        ...e,
        isWorking,
        // If switching to "not working", default to all-day off
        allDay: !isWorking ? true : false,
      };
    }));
  };

  const handleDelete = async (entryId: string) => {
    try {
      await onDelete({ variables: { id: entryId } });
      toast.success('Entry deleted successfully');
    } catch (error: any) {
      console.error('Error deleting entry:', error);
      toast.error(`Failed to delete entry: ${error.message}`);
    }
  };

  const timeToDbFormat = (time: Dayjs | null): string | null => {
    if (!time) return null;
    return time.format('HH:mm:ss');
  };

  const handleSave = async () => {
    for (const entry of newEntries) {
      if (!entry.name.trim()) {
        toast.warning('Please enter a reason for all entries');
        return;
      }
      if (!entry.date) {
        toast.warning('Please select a date for all entries');
        return;
      }
      if (entry.date.isBefore(dayjs(), 'day')) {
        toast.warning('Cannot add one-off days in the past');
        return;
      }
      const needsTimes = entry.isWorking || (!entry.isWorking && !entry.allDay);
      if (needsTimes && (!entry.openTime || !entry.closedTime)) {
        toast.warning('Please select start and end times for all entries with specific hours');
        return;
      }
    }

    try {
      const entriesToCreate = newEntries.map((entry) => {
        // Times are stored when: working (always), or not-working partial day
        const hasTimes = entry.isWorking || (!entry.isWorking && !entry.allDay);
        return {
          ...(config.entityType === 'driver'
            ? { driver_id: Number(entityId) }
            : { location_id: Number(entityId) }
          ),
          name: entry.name.trim(),
          type: 'one-off' as const,
          day: entry.date!.date(),
          month: entry.date!.month() + 1,
          year: entry.date!.year(),
          is_open: entry.isWorking,
          open_time: hasTimes ? timeToDbFormat(entry.openTime) : null,
          closed_time: hasTimes ? timeToDbFormat(entry.closedTime) : null,
          created_by: userName,
          created_at: 'now()',
          updated_by: userName,
          updated_at: 'now()',
        };
      });

      await onCreate(entriesToCreate);

      toast.success(
        newEntries.length === 1
          ? 'Entry added successfully'
          : `${newEntries.length} entries added successfully`
      );
      resetForm();
    } catch (error) {
      console.error('Error adding entries:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to add entries: ${errorMessage}`);
    }
  };

  return (
    <Modal open={open} handleClose={onClose} width="md">
      <ModalHeader>
        <>
          {labels.title}
          <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {timezone}
          </Typography>
        </>
      </ModalHeader>

      <ModalContent>
        {/* Existing entries */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Scheduled Overrides
          </Typography>

          {processedOneOffDays.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {labels.noEntriesText}
            </Typography>
          ) : (
            <List sx={{ p: 0, border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
              {/* Upcoming entries */}
              {upcomingEntries.map((entry, index) => (
                <React.Fragment key={entry.id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => handleDelete(entry.id)}
                        disabled={loading}
                        color="error"
                        size="small"
                      >
                        <BiTrash />
                      </IconButton>
                    }
                    sx={{ py: 1 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {entry.name}
                          </Typography>
                          <Chip
                            label={entry.isWorking ? 'Working' : 'Off'}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: 11,
                              bgcolor: entry.isWorking ? '#f3fbf4' : '#fff2f4',
                              color: entry.isWorking ? '#20b020' : '#f02020',
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <>
                          {entry.date}
                          {entry.openTime && entry.closedTime && (
                            <Typography component="span" variant="caption" display="block" color="text.secondary">
                              {entry.isWorking ? 'Available' : 'Unavailable'}: {entry.openTime} – {entry.closedTime}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}

              {/* Past entries (grayed out) */}
              {pastEntries.length > 0 && upcomingEntries.length > 0 && <Divider />}
              {pastEntries.map((entry, index) => (
                <React.Fragment key={entry.id}>
                  {index > 0 && <Divider />}
                  <ListItem sx={{ py: 1, opacity: 0.5 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {entry.name}
                          </Typography>
                          <Chip
                            label={entry.isWorking ? 'Working' : 'Off'}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: 11,
                              bgcolor: 'grey.100',
                              color: 'text.secondary',
                            }}
                          />
                        </Box>
                      }
                      secondary={`${entry.date} (past)`}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Add New Entries Form */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Add Override
          </Typography>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
              {newEntries.map((entry) => (
                <Box
                  key={entry.id}
                  sx={{
                    p: 2,
                    bgcolor: 'grey.50',
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {/* Row 1: Working/Not Working toggle + Reason + Date */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    {/* Working / Not Working toggle */}
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleWorking(entry.id, true)}
                        sx={{
                          width: 32,
                          height: 32,
                          border: '2px solid',
                          borderColor: entry.isWorking ? '#20b020' : 'grey.300',
                          bgcolor: entry.isWorking ? '#e8f5e8' : 'transparent',
                          color: entry.isWorking ? '#20b020' : 'grey.400',
                          '&:hover': {
                            bgcolor: entry.isWorking ? '#d4ecd4' : 'grey.100',
                          },
                        }}
                      >
                        <BiCheck size={18} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleWorking(entry.id, false)}
                        sx={{
                          width: 32,
                          height: 32,
                          border: '2px solid',
                          borderColor: !entry.isWorking ? '#f02020' : 'grey.300',
                          bgcolor: !entry.isWorking ? '#fff2f4' : 'transparent',
                          color: !entry.isWorking ? '#f02020' : 'grey.400',
                          '&:hover': {
                            bgcolor: !entry.isWorking ? '#ffe0e4' : 'grey.100',
                          },
                        }}
                      >
                        <BiX size={18} />
                      </IconButton>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        {entry.isWorking ? 'Working' : 'Off'}
                      </Typography>
                    </Box>

                    <TextField
                      label="Reason"
                      value={entry.name}
                      onChange={(e) => handleEntryChange(entry.id, 'name', e.target.value)}
                      size="small"
                      placeholder={entry.isWorking ? 'e.g., Covering a shift' : 'e.g., Dentist, PTO'}
                      disabled={loading}
                      sx={{ flex: 1, minWidth: 180 }}
                    />

                    <DatePicker
                      label="Date"
                      value={entry.date}
                      onChange={(value) => handleEntryChange(entry.id, 'date', value)}
                      minDate={dayjs()}
                      disabled={loading}
                      slotProps={{
                        textField: { size: 'small', sx: { minWidth: 150 } },
                      }}
                    />

                    {newEntries.length > 1 && (
                      <IconButton
                        onClick={() => handleDeleteEntry(entry.id)}
                        disabled={loading}
                        size="small"
                        color="error"
                      >
                        <BiTrash />
                      </IconButton>
                    )}
                  </Box>

                  {/* Row 2: Time pickers */}
                  {/* Working: always show times (when are they available?) */}
                  {/* Not working: show "All day" checkbox, times only if not all day */}
                  <Box sx={{ mt: 1.5, pl: 0.5 }}>
                    {entry.isWorking ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <TimePicker
                          label="Available From"
                          value={entry.openTime}
                          onChange={(value) => handleEntryChange(entry.id, 'openTime', value)}
                          disabled={loading}
                          slotProps={{ textField: { size: 'small' } }}
                        />
                        <Typography variant="body2" color="text.secondary">to</Typography>
                        <TimePicker
                          label="Until"
                          value={entry.closedTime}
                          onChange={(value) => handleEntryChange(entry.id, 'closedTime', value)}
                          disabled={loading}
                          slotProps={{ textField: { size: 'small' } }}
                        />
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={entry.allDay}
                              onChange={(e) => handleEntryChange(entry.id, 'allDay', e.target.checked)}
                              disabled={loading}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">All day</Typography>}
                          sx={{ m: 0 }}
                        />
                        {!entry.allDay && (
                          <>
                            <TimePicker
                              label="Unavailable From"
                              value={entry.openTime}
                              onChange={(value) => handleEntryChange(entry.id, 'openTime', value)}
                              disabled={loading}
                              slotProps={{ textField: { size: 'small' } }}
                            />
                            <Typography variant="body2" color="text.secondary">to</Typography>
                            <TimePicker
                              label="Until"
                              value={entry.closedTime}
                              onChange={(value) => handleEntryChange(entry.id, 'closedTime', value)}
                              disabled={loading}
                              slotProps={{ textField: { size: 'small' } }}
                            />
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}

              <Button
                startIcon={<BiPlus />}
                onClick={handleAddEntry}
                disabled={loading}
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  py: 1,
                  '&:hover': {
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    bgcolor: 'action.hover',
                  },
                }}
              >
                Add Another Override
              </Button>
            </Box>
          </LocalizationProvider>
        </Box>
      </ModalContent>

      <ModalFooter>
        <ModalAction onClick={onClose} disabled={loading}>
          CANCEL
        </ModalAction>

        <ModalAction onClick={handleSave} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'SAVE'}
        </ModalAction>
      </ModalFooter>
    </Modal>
  );
};

export default OneOffDaysManager;
